import PublishIcon from '@mui/icons-material/Publish';
import {Avatar, Box, Grid, Typography} from '@mui/material';
import {useNotification} from '@ozark/crm/src/hooks/useNotification';
import {useCallback, useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {readString} from 'react-papaparse';
import {useCallable, useUserInfo} from '../../../hooks';
import {Import} from './Import';

interface ImportAdjustmentProps {
  onLoad: () => void;
  yearMonths: string[] | null;
}
export const ImportAdjustment: React.FC<ImportAdjustmentProps> = ({onLoad, yearMonths}) => {
  const {addImportedResidualManualAdjustments} = useCallable();
  const showNotification = useNotification();
  const [defaultValues, setDefaultValues] = useState<object | null>(null);
  const [csvObjects, setCsvObjects] = useState<object[] | null>();
  const [csvBuffer, setCsvBuffer] = useState<ArrayBuffer | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const {isErpUser} = useUserInfo();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach((file: File) => {
      const reader = new FileReader();
      reader.onerror = () => console.error('file reading has failed');
      reader.onload = async () => {
        setCsvBuffer(reader.result as ArrayBuffer);
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    accept: ['csv', '.csv', 'text/csv'],
    maxFiles: 1,
  });

  const reset = () => {
    setDefaultValues(null);
    setCsvObjects(null);
    setCsvBuffer(null);
    setDragActive(false);
  };

  useEffect(() => {
    if (csvBuffer) {
      const encoding = new TextDecoder('utf-8');
      const text = encoding.decode(csvBuffer);
      (async () => {
        // @ts-ignore
        const csv = readString(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (e: string) => {
            return e.trim();
          },
          transform: (e: string) => {
            return e.trim();
          },
          // @ts-ignore
        }).data;

        const defaultValues = isErpUser
          ? {
              'columns.agentName': 'Agent Name',
              'columns.agentCode': 'Agent Code',
              'columns.group': 'Group Name',
              'columns.description': 'Description',
              'columns.amount': 'Amount',
              'columns.revShare': 'Rev Share',
            }
          : {
              'columns.agentName': 'Agent Name',
              'columns.agentCode': 'Agent Code',
              'columns.description': 'Description',
              'columns.amount': 'Amount',
              'columns.revShare': 'Rev Share',
            };
        setCsvObjects(csv as any);
        setDefaultValues(defaultValues);
      })();
    }
    // eslint-disable-next-line
  }, [csvBuffer, isErpUser]);

  const handleSubmit = async (data: any) => {
    setIsImporting(true);
    const saveResult = await addImportedResidualManualAdjustments({
      yearMonth: data.yearMonth,
      importedRawAdjustmentData: data.columns,
      fileUrl: data.fileUrl,
    });

    if (saveResult.status === 'error') {
      showNotification('error', saveResult.message);
      setIsImporting(false);
      return;
    }

    if (saveResult.status === 'ok' && saveResult?.warnings && saveResult?.warnings?.length > 0) {
      showNotification(
        'warning',
        `Some adjustments won't be saved: \n ${saveResult.warnings.join(', \n')}`
      );

      console.error(saveResult.warnings.join(', \n'));
    } else if (saveResult.status === 'ok') {
      showNotification('success', 'Saved successfully');
    }
    reset();
    onLoad();
    setIsImporting(false);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!csvObjects && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  {...getRootProps()}
                  marginTop={'20vh'}
                  width={500}
                  height={160}
                  display={'flex'}
                  px={6}
                  marginX={'auto'}
                  border={`dashed 4px ${dragActive ? '#1877f2' : '#b2c2cd'}`}
                  borderRadius="20px"
                  flexDirection={'column'}
                  alignItems={'center'}
                  justifyContent={'space-evenly'}
                  onDragOver={() => setDragActive(true)}
                  onDragLeave={() => setDragActive(false)}
                >
                  <input {...getInputProps()} />
                  <Typography variant="h4">Import Adjustment File</Typography>
                  <Avatar>
                    <PublishIcon color={dragActive ? 'action' : 'inherit'} />
                  </Avatar>
                </Box>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      {csvObjects && csvBuffer && defaultValues && (
        <Import
          onSubmit={handleSubmit}
          isLoading={isImporting}
          onCancel={reset}
          csvObjects={csvObjects}
          csvBuffer={csvBuffer}
          defaultValues={defaultValues}
          yearMonths={yearMonths}
        />
      )}
    </>
  );
};
